import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft } from "lucide-react";
import { Section, SectionHeader } from "@/components/site/Section";
import { DimensionBars } from "@/components/site/DimensionBars";
import { GapLists } from "@/components/site/GapLists";
import { LockedPanel } from "@/components/site/LockedPanel";
import { AdvisorCTA } from "@/components/site/AdvisorCTA";
import { ANON_THRESHOLD, relationshipLabels } from "@/content/review";
import { getCycleReport, requestAdvisorConversation } from "@/utils/review.functions";
import type { ReportPayload } from "@/content/review";

export const Route = createFileRoute("/_authenticated/my-review/$cycleId/results")({
  head: () => ({
    meta: [
      { title: "Your 360 results | Leadership Edge Live" },
      {
        name: "description",
        content: "Where your own view of how you lead and everyone else's view part company.",
      },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Your 360 results" },
      {
        property: "og:description",
        content: "Self view against everyone else, across 16 behaviors.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: ResultsPage,
});

function ResultsPage() {
  const { cycleId } = useParams({ from: "/_authenticated/my-review/$cycleId/results" });
  const load = useServerFn(getCycleReport);
  const notifyAdvisor = useServerFn(requestAdvisorConversation);
  const [report, setReport] = useState<ReportPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    load({ data: { cycleId } })
      .then(setReport)
      .catch(() => setError("We couldn't load these results."));
  }, [load, cycleId]);

  if (error || !report) {
    return (
      <Section>
        <p className="text-sm text-muted-foreground">{error ?? "Working out your results."}</p>
      </Section>
    );
  }

  // Narrow the union once: the locked preview carries no behaviour detail.
  const full = report.unlocked ? report : null;
  const locked = full === null;

  return (
    <>
      <Section>
        <Link
          to="/my-review/$cycleId/"
          params={{ cycleId }}
          className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-primary"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back to this check
        </Link>

        <SectionHeader
          eyebrow={locked ? "Free preview" : "Full report"}
          title="Where your view and everyone else's part company"
          intro="Each bar is an average of the behaviors in that dimension, on the same five-point scale everyone answered on."
          titleClassName="text-3xl font-semibold leading-tight text-ink md:text-4xl"
        />

        <DimensionBars bars={report.dimensions} />

        <p className="mt-8 rounded-2xl border border-border/60 bg-background p-6 text-sm leading-relaxed text-muted-foreground">
          <span className="font-semibold text-ink">{report.blindSpotCount}</span> behaviors where
          you rate yourself higher than others do.
        </p>
      </Section>

      <Section tone="lavender">
        {full === null ? (
          <LockedPanel
            title="The behavior detail, the group breakdown and the courses"
            body="Which behaviors are blind spots, where others rate you higher than you rate yourself, where everyone agrees it is weak, how each group answered, and the courses that close those gaps. A Development Pro walks you through it and opens this view."
          />
        ) : (
          <div className="space-y-14">
            <GapLists
              title="Blind spots"
              intro="You rate yourself higher here than the people around you do."
              rows={full.blindSpots}
              emptyLine="No blind spots stand out yet."
            />
            <GapLists
              title="Hidden strengths"
              intro="Others see more of this in you than you give yourself credit for."
              rows={full.hiddenStrengths}
              emptyLine="Nothing here yet."
            />
            <GapLists
              title="Agreed gaps"
              intro="You and everyone else land low on these. Start here."
              rows={full.agreedGaps}
              showCourses
              emptyLine="No behavior is low on both sides."
            />
          </div>
        )}
      </Section>

      {full !== null && (
        <Section>
          <SectionHeader
            eyebrow="Group by group"
            title="How each group answered"
            intro={`Direct reports and peers appear only once ${ANON_THRESHOLD} people in that group have answered. Your manager is one person, so that line is labeled.`}
            titleClassName="text-3xl font-semibold leading-tight text-ink md:text-4xl"
          />
          <div className="grid gap-5 md:grid-cols-3">
            {full.groups.map((group) => {
              const breakdown = full.groupBreakdown.find(
                (b) => b.relationship === group.relationship,
              );
              const average = breakdown?.rows.length
                ? breakdown.rows.reduce((total, row) => total + row.avg, 0) / breakdown.rows.length
                : null;
              return (
                <div
                  key={group.relationship}
                  className="rounded-2xl border border-border/60 bg-background p-7"
                >
                  <h3 className="font-display text-lg font-semibold text-ink">
                    {relationshipLabels[group.relationship]}
                  </h3>
                  {group.identified && (
                    <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-primary">
                      Identified, not anonymous
                    </p>
                  )}
                  {group.eligible && average !== null ? (
                    <p className="mt-3 text-sm text-muted-foreground">
                      Average <span className="font-semibold text-ink">{average.toFixed(1)}</span>{" "}
                      from {group.submitted} {group.submitted === 1 ? "response" : "responses"}.
                    </p>
                  ) : (
                    <p className="mt-3 text-sm text-muted-foreground">
                      Waiting on {group.needed} more {group.needed === 1 ? "response" : "responses"}
                      . Nothing is shown until then.
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </Section>
      )}

      <AdvisorCTA
        title="Gaps are only useful once someone acts on them."
        body="A Development Pro reads the report with you and picks the two or three behaviors worth working on first."
        primaryLabel="Talk to a Development Pro"
        primaryTo="/contact/"
        onPrimaryClick={() => {
          // Tells the support inbox which check to pull up. Never blocks the click.
          void notifyAdvisor({ data: { cycleId } }).catch(() => undefined);
        }}
        secondaryLabel="Browse the course catalog"
        secondaryTo="/course-catalog/"
      />
    </>
  );
}
